import React, { useEffect, useRef, useState } from 'react';
import './SponsorCard.css';

// Shared sponsor card used by the homepage marquee and the /sponsors page.
//
// variant: 'full'    – large card with scroll-triggered staggered fade-in (Sponsors page)
//          'marquee' – compact card, always visible (homepage sliding strip)
// isClone: true for cards inside the marquee's duplicated track – removed from
//          the tab order so keyboard users don't hit every link twice.
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const SponsorCard = ({ sponsor, index = 0, variant = 'full', isClone = false }) => {
  const cardRef = useRef(null);
  const isMarquee = variant === 'marquee';
  const reducedMotion = prefersReducedMotion();
  const [isVisible, setIsVisible] = useState(isMarquee || reducedMotion);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (isMarquee || reducedMotion) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), index * 150);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [index, isMarquee, reducedMotion]);

  const handleMouseMove = (e) => {
    if (reducedMotion) return; // no tilt for reduced-motion users
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 8;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -8;
    setTilt({ x, y });
  };

  const handleMouseLeave = () => setTilt({ x: 0, y: 0 });

  const hasUrl = sponsor.url && sponsor.url !== '#';
  const Wrapper = hasUrl ? 'a' : 'div';
  const linkProps = hasUrl
    ? { href: sponsor.url, target: '_blank', rel: 'noopener noreferrer' }
    : {};
  if (isClone) linkProps.tabIndex = -1;

  const logos = sponsor.logos || [{ img: sponsor.img, alt: sponsor.alt }];

  return (
    <Wrapper
      {...linkProps}
      className={`sponsor-card sponsor-card--${variant} sponsor-card--${sponsor.tier.toLowerCase()} ${isVisible ? 'sponsor-card--visible' : ''}`}
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={
        reducedMotion
          ? undefined
          : { transform: `perspective(800px) rotateY(${tilt.x}deg) rotateX(${tilt.y}deg)` }
      }
    >
      <div className="sponsor-card__glow" />
      <span className={`sponsor-card__tier sponsor-card__tier--${sponsor.tier.toLowerCase()}`}>
        {sponsor.tier} Sponsor
      </span>
      <div className={`sponsor-card__img-wrap${logos.length > 1 ? ' sponsor-card__img-wrap--multi' : ''}`}>
        {logos.map((logo, i) => (
          <img key={i} src={logo.img} alt={logo.alt} />
        ))}
      </div>
      <p className="sponsor-card__name">{sponsor.name}</p>
      {hasUrl && (
        <span className="sponsor-card__cta">
          Visit Website
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 17L17 7" />
            <path d="M7 7h10v10" />
          </svg>
        </span>
      )}
    </Wrapper>
  );
};

export default SponsorCard;
